// API Configuration
// Note: This app uses Vite, so environment variables use VITE_ prefix (not REACT_APP_)
// API URLs are automatically generated from the browser URL
// For development, you can set these values directly or create a .env file with:
// VITE_SEARCH_API_KEY=your-api-key
// VITE_SEARCH_API_BASE_URL=your-base-url

import {
  getCurrentClient,
  hasValidSubdomain,
  getSubdomain,
  getEnvironmentInfo,
} from "../utils/subdomainUtils";
import {
  generateAuthServiceUrlWithLogging,
  generateApiUrl,
} from "../utils/authUtils";
import { getFetchedConfig } from "../configService";

interface ApiConfig {
  client: {
    id: string | null;
    isValid: boolean;
    environment: string;
    domain: string;
    region: string | null | undefined;
    shouldUseMocking: boolean;
  };
  search: {
    baseUrl: string;
    apiKey: string;
    tenantId: string;
    indexName: string;
    contentType: string;
  };
  auth: {
    sessionUrl: string;
    environment: string;
  };
  app: {
    apiUrl: string;
    environment: string;
    version: string;
  };
}

// Helper function to get environment variable with fallback
const getEnvVar = (key: string, fallback?: string): string => {
  const config = getFetchedConfig();
  const value = config[key];

  // Type guard to ensure the value is a string before returning
  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  // If the value is not a string, or is empty, return the fallback
  return fallback || "";
};

// Lazy initialization to avoid environment variable timing issues
let _apiConfig: ApiConfig | null = null;

function createApiConfig() {
  // Get current client for multi-tenant support (called at runtime, not import time)
  const currentClient = getCurrentClient();
  const envInfo = getEnvironmentInfo();

  return {
    // Client configuration (always required)
    client: {
      id: currentClient,
      isValid: hasValidSubdomain(),
      environment: envInfo.environment,
      domain: envInfo.domain,
      region: envInfo.region,
      shouldUseMocking: envInfo.shouldUseMocking,
    },

    // Search API Configuration
    search: {
      baseUrl: getEnvVar("VITE_SEARCH_API_BASE_URL"),
      apiKey: getEnvVar("VITE_SEARCH_API_KEY"),
      tenantId: currentClient || "unknown", // Use client as tenant ID
      indexName: "idx_test_policy_manager",
      contentType: "policy_manager",
    },

    // Auth Service Configuration (environment-specific)
    auth: {
      sessionUrl: (() => {
        // Local development: use our local /session endpoint (returns raw JWT payload)
        if (envInfo.environment === "localhost" || envInfo.shouldUseMocking) {
          // Use the same dynamic API URL generation for consistency
          const apiUrl = generateApiUrl();
          const baseUrl = apiUrl.replace("/api/v1", ""); // Remove /api/v1 prefix for session endpoint

          // Remove trailing slash to avoid double slashes
          const cleanBaseUrl = baseUrl.replace(/\/$/, "");
          return `${cleanBaseUrl}/session`;
        }

        // AWS production: use external auth service (returns raw JWT payload)
        // For dual domain support, use utility function to generate auth service URL
        return generateAuthServiceUrlWithLogging(
          envInfo.domain,
          envInfo.environment
        );
      })(),
      environment: envInfo.environment,
    },

    // Other API configurations can be added here
    app: {
      apiUrl: generateApiUrl(),
      environment: getEnvVar("VITE_ENVIRONMENT", "development"),
      version: getEnvVar("VITE_VERSION", "1.0.0"),
    },
  };
}

// Export getter function instead of direct object
export const API_CONFIG: ApiConfig = new Proxy({} as ApiConfig, {
  get(_target, prop: keyof ApiConfig) {
    if (!_apiConfig) {
      _apiConfig = createApiConfig();
    }
    return _apiConfig[prop];
  },
});

// Log configuration warnings in development (defer to avoid import-time execution)
setTimeout(() => {
  if (
    API_CONFIG.app.environment === "development" ||
    API_CONFIG.client.environment === "localhost"
  ) {
    console.group("🔧 Policy Manager Configuration");
    console.log("Environment:", API_CONFIG.client.environment);
    console.log("Domain:", API_CONFIG.client.domain);
    console.log("Region:", API_CONFIG.client.region || "us");
    console.log("Valid subdomain:", API_CONFIG.client.isValid);
    console.log("Client ID:", API_CONFIG.client.id || "MISSING");
    console.log("API URL:", API_CONFIG.app.apiUrl);
    console.log("Auth Session URL:", API_CONFIG.auth.sessionUrl);
    console.log("Search Tenant ID:", API_CONFIG.search.tenantId);
    console.log("Mocking enabled:", API_CONFIG.client.shouldUseMocking);

    if (!API_CONFIG.search.apiKey) {
      console.warn(
        "⚠️ VITE_SEARCH_API_KEY is not set. Search functionality will not work. Please add it to your .env file."
      );
    }

    if (!API_CONFIG.client.isValid) {
      const appSubdomain = getSubdomain();
      console.error(
        `❌ INVALID SUBDOMAIN: Please access via <client>.${appSubdomain}.highbond.localhost or supported domain formats`
      );
    }

    if (!API_CONFIG.client.id) {
      console.error(
        "❌ NO CLIENT ID: Client identifier is required in subdomain"
      );
    }

    console.groupEnd();
  }
}, 100);

export default API_CONFIG;
